'use client';

import { Typography } from '@components/typography';
import { ReactNode, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Control } from './Control';

export interface TabSectionItem {
  id: string;
  tab: string;
}

interface TabsContentSectionProps<T extends TabSectionItem> {
  items: T[];
  children: (item: T) => ReactNode;
}

export const TabsContentSection = <T extends TabSectionItem>({
  items,
  children,
}: TabsContentSectionProps<T>) => {
  const [activeItemId, setActiveItemId] = useState(items[0]?.id ?? '01');
  const activeItem =
    items.find((item) => item.id === activeItemId) ?? items[0] ?? null;

  if (!activeItem) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4 px-4 max-sm:pr-0">
      <div className="flex gap-4 max-sm:hidden">
        {items.map((item) => {
          const isActive = item.id === activeItemId;

          return (
            <Control
              key={item.id}
              active={isActive}
              onClick={() => setActiveItemId(item.id)}
              className="flex flex-1 basis-0 justify-center"
            >
              <Typography
                variant="text-base"
                color={isActive ? 'primary' : 'secondary'}
              >
                {item.tab}
              </Typography>
            </Control>
          );
        })}
      </div>

      <div className="flex md:hidden">
        <Swiper spaceBetween={12} slidesPerView={1.5}>
          {items.map((item) => {
            const isActive = item.id === activeItemId;

            return (
              <SwiperSlide key={item.id}>
                <Control
                  active={isActive}
                  onClick={() => setActiveItemId(item.id)}
                  className="flex w-full justify-center whitespace-nowrap"
                >
                  <Typography
                    variant="text-base"
                    color={isActive ? 'primary' : 'secondary'}
                  >
                    {item.tab}
                  </Typography>
                </Control>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>

      {children(activeItem)}
    </div>
  );
};
